<template>
  <div :class="containerClass">
    <page-header
      :title="this.$route.params.id ? 'Edit Organization' : 'Add Organization'"
      :breadcrumb="breadcrumb"
      :container-class="null"
      class="mb-32pt"
    />

    <page-separator title="Organization's Info" />

    <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit">
      <!-- Logo -->
      <b-form-group class="row-align-items-center">
        <label class="form-label">
          Logo
          <i
            class="material-icons icon-16pt clickable-item text-muted"
            v-b-popover.hover.top="`Ideal dimensions for the logo should be 1280*720.`"
            >info</i
          >
        </label>
        <b-media class="align-items-center" vertical-align="center">
          <fmv-avatar :title="true" rounded size="lg" no-link slot="aside">
            <span v-if="isLogoUploading">...</span>
            <b-img
              :src="organization.logoUrl"
              class="img-fluid"
              width="40"
              alt="Logo"
              v-else-if="organization.logoUrl"
            />
            <i class="fas fa-university fa-lg" v-else></i>
          </fmv-avatar>

          <image-uploader
            @image="e => setImage(e, 'logo')"
            :isUploading="isLogoUploading"
            :isRequired="!organization.logoUrl"
            placeholder="Select Organization's Logo Picture"
          />
        </b-media>
      </b-form-group>

      <!-- Small Logo -->
      <b-form-group label-for="small_logo_url" class="row-align-items-center">
        <label class="form-label">
          Small Logo
          <i
            class="material-icons icon-16pt clickable-item text-muted"
            v-b-popover.hover.top="
              `Ideal dimensions for small logo should be 512*512. This will be shown as app's favicon.`
            "
            >info</i
          >
        </label>
        <b-media class="align-items-center" vertical-align="center">
          <fmv-avatar :title="true" rounded size="lg" no-link slot="aside">
            <span v-if="isSmallLogoUploading">...</span>
            <b-img
              :src="organization.small_logo_url"
              class="img-fluid"
              width="40"
              alt="Logo"
              v-else-if="organization.small_logo_url"
            />
            <i class="fas fa-university fa-lg" v-else></i>
          </fmv-avatar>

          <image-uploader
            @image="e => setImage(e, 'smallLogo')"
            :isUploading="isSmallLogoUploading"
            :isRequired="!organization.small_logo_url"
            placeholder="Select Organization's Small Logo Picture"
          />
        </b-media>
      </b-form-group>

      <b-form-group>
        <b-form-checkbox id="checkbox-4" v-model="is_published" name="checkbox-4" switch
          >Website Published?
        </b-form-checkbox>
      </b-form-group>

      <b-form-group label="Linked Website" label-for="linked-website" label-class="form-label">
        <b-form-input id="linked-website" placeholder="Enter linked website domain" v-model="wbst_domain" required />
      </b-form-group>

      <b-form-group label="Community Link" label-for="community-link" label-class="form-label">
        <b-form-input id="community-link" placeholder="Enter community link" v-model="communityLink" />
      </b-form-group>

      <!-- Name -->
      <b-form-group label="Name" label-for="name" label-class="form-label">
        <b-form-input id="name" placeholder="Enter Organization's Name" v-model="organization.name" required />
      </b-form-group>

      <b-form-group label="Contact Email" label-for="email" label-class="form-label">
        <b-form-input
          id="email"
          placeholder="Enter Organization's Contact Email"
          v-model="organization.email"
          type="email"
        />
      </b-form-group>

      <b-form-group label="Organization Type" label-for="organization-type" label-class="form-label">
        <v-select
          id="organization-type"
          class="form-control v-select-custom"
          label="text"
          v-model="organizationType"
          :reduce="item => item.value"
          placeholder="Select Organization Type"
          :options="OrganizationTypeOptions"
        >
        </v-select>

        <!-- <b-form-input
          id="other-type"
          class="mt-2"
          placeholder="Please specify."
          v-model="otherOrganizationType"
          required
          v-if="organizationType.includes('other')"
        /> -->
      </b-form-group>

      <!-- Phone -->
      <b-form-group :label="$t('schoolMsgs.phone')" label-for="phone" label-class="form-label">
        <b-form-input
          id="phone"
          placeholder="(888) 689 - 1235"
          v-model="organization.phone"
          v-mask="'(###) ### - ####'"
          required
        />
      </b-form-group>

      <!-- Address Fields -->
      <b-form-group :label="$t('schoolMsgs.address')" label-for="address" label-class="form-label">
        <b-form-input id="address" :placeholder="$t('schoolMsgs.address')" v-model="organization.address" required />
      </b-form-group>

      <div class="row">
        <b-form-group :label="$t('schoolMsgs.zipcode')" label-for="zipcode" label-class="form-label" class="col-md-6">
          <b-form-input id="zipcode" :placeholder="$t('schoolMsgs.zipcode')" v-model="organization.zipcode" required />
        </b-form-group>

        <b-form-group :label="$t('schoolMsgs.city')" label-for="city" label-class="form-label" class="col-md-6">
          <b-form-input id="city" :placeholder="$t('schoolMsgs.city')" v-model="organization.city" required />
        </b-form-group>
      </div>

      <div class="row">
        <b-form-group :label="$t('schoolMsgs.state')" label-for="state" label-class="form-label" class="col-md-6">
          <v-select
            id="state"
            class="form-control v-select-custom"
            label="text"
            v-model="organization.state"
            :reduce="state => state.value"
            placeholder="Select State"
            :options="stateOptions"
          >
            <template #search="{ attributes, events }">
              <input class="vs__search" :required="!organization.state" v-bind="attributes" v-on="events" />
            </template>
          </v-select>
        </b-form-group>

        <b-form-group :label="$t('schoolMsgs.country')" label-for="country" label-class="form-label" class="col-md-6">
          <v-select
            id="country"
            class="form-control v-select-custom"
            label="text"
            disabled
            v-model="organization.country"
            :reduce="country => country.value"
            :placeholder="$t('schoolMsgs.countryPlaceholder')"
            :options="countryOptions"
          >
            <template #search="{ attributes, events }">
              <input class="vs__search" :required="!organization.country" v-bind="attributes" v-on="events" />
            </template>
          </v-select>
        </b-form-group>
      </div>

      <!-- Other Details -->

      <b-form-group label="Short Bio" label-for="short_bio" label-class="form-label">
        <b-form-textarea
          id="short_bio"
          placeholder="Enter Organization's Short/Summarized Bio."
          :rows="5"
          v-model="organization.short_bio"
        />
      </b-form-group>
      <b-form-group label="Bio" label-for="bio" label-class="form-label">
        <editor
          :init="{
            height: 400,
            menubar: 'edit view insert format tools table help',
            plugins: [
              'advlist autolink lists link image charmap print preview anchor',
              'searchreplace visualblocks code fullscreen',
              'insertdatetime media table paste code help wordcount'
            ],
            toolbar:
              'undo redo | formatselect | bold italic backcolor | \
           alignleft aligncenter alignright alignjustify | \
           bullist numlist outdent indent | removeformat | help'
          }"
          v-model="organization.bio"
          placeholder="Enter Organization's Bio"
        />
      </b-form-group>
      <b-form-group label="Career Pathways" label-for="offeredCareers" label-class="form-label">
        <v-select
          id="offeredCareers"
          class="form-control v-select-custom"
          label="name"
          v-model="organization.offeredCareers"
          :reduce="item => item.id"
          placeholder="All Career Pathways By Default"
          :options="allCareers"
          :loading="areCareersLoading"
          multiple
        >
        </v-select>
      </b-form-group>
      <b-form-group
        :label="$t('schoolMsgs.profileBgImage')"
        label-for="profileBgImage"
        label-class="form-label"
        class="row-align-items-center"
      >
        <b-media class="align-items-center" vertical-align="center">
          <image-uploader
            @image="e => setImage(e, 'profileBgImage')"
            :isUploading="isProfileBgImgUploading"
            :isRequired="false"
            placeholder="Select Organization's Profile Background Image"
          />
        </b-media>
      </b-form-group>

      <div class="row">
        <b-form-group
          :label="$t('schoolMsgs.primaryColor')"
          label-for="primaryColor"
          label-class="form-label"
          class="col-md-4"
        >
          <v-swatches
            v-model.trim="organization.primaryColor"
            shapes="circles"
            show-fallback
            :swatches="swatches"
            fallback-input-type="color"
            :fallback-ok-text="$t('generalMsgs.select')"
            fallback-input-class="py-0"
          ></v-swatches>
        </b-form-group>
        <b-form-group
          :label="$t('schoolMsgs.secondaryColor')"
          label-for="secondaryColor"
          label-class="form-label"
          class="col-md-4"
        >
          <v-swatches
            v-model.trim="organization.secondaryColor"
            shapes="circles"
            show-fallback
            :swatches="swatches"
            fallback-input-type="color"
            :fallback-ok-text="$t('generalMsgs.select')"
            fallback-input-class="py-0"
          ></v-swatches>
        </b-form-group>
        <b-form-group
          :label="$t('schoolMsgs.darkColor')"
          label-for="darkColor"
          label-class="form-label"
          class="col-md-4"
        >
          <v-swatches
            v-model.trim="organization.darkColor"
            shapes="circles"
            show-fallback
            :swatches="swatches"
            fallback-input-type="color"
            :fallback-ok-text="$t('generalMsgs.select')"
            fallback-input-class="py-0"
          ></v-swatches>
        </b-form-group>
      </div>
      <page-separator title="Credit's Info & Employer's Configuration" />

      <b-form-group label="Available Tokens / Credits" label-for="available_tokens" label-class="form-label">
        <currency-input
          v-model="organization.available_tokens"
          required
          class="form-control"
          placeholder="Enter available tokens"
          :currency="{ prefix: '', suffix: ' TT' }"
          :precision="0"
          :allow-negative="false"
        />
      </b-form-group>

      <b-form-group label="Available Job Credits" label-for="available_job_credits" label-class="form-label">
        <currency-input
          v-model="organization.available_job_credits"
          required
          class="form-control"
          :currency="{ prefix: '', suffix: '' }"
          placeholder="Enter job credit "
          :precision="0"
        />
      </b-form-group>

      <b-form-group>
        <b-form-checkbox id="checkbox-5" v-model="organization.allow_emp_deduct" name="checkbox-5" switch
          >Allow Employer Deduction?
        </b-form-checkbox>
      </b-form-group>

      <b-form-group>
        <b-form-checkbox id="checkbox-6" v-model="organization.show_all_school" name="checkbox-6" switch
          >Allow All Transition Schools?
        </b-form-checkbox>
      </b-form-group>

      <b-form-group>
        <b-form-checkbox id="checkbox-7" v-model="organization.hide_purchase_btn" name="checkbox-7" switch
          >Hide Purchase Button for Employers?
        </b-form-checkbox>
      </b-form-group>

      <b-form-group>
        <b-form-checkbox id="checkbox-8" v-model="organization.enable_discussions" name="checkbox-8" switch
          >Enable Discussions?
        </b-form-checkbox>
      </b-form-group>

      <b-form-group>
        <b-form-checkbox id="checkbox-9" v-model="organization.enable_bulk_messaging" name="checkbox-9" switch
          >Enable Bulk SMS/Email?
        </b-form-checkbox>
      </b-form-group>

      <!-- Sidebar Tools -->
      <page-separator title="Sidebar Tools" />
      <b-form-group>
        <div class="d-flex align-items-center justify-content-between mb-2">
          <p class="text-muted mb-0">
            Choose which tools appear in this organization's sidebar. Discussions, Interviews, Research and Events also
            control the matching cards on the student dashboard. Website hides the whole Website section.
          </p>
          <b-btn variant="link" size="sm" class="p-0" @click.prevent="toggleAllSidebarTools">
            {{ allSidebarToolsEnabled ? 'Disable all' : 'Enable all' }}
          </b-btn>
        </div>

        <div class="list-group">
          <div
            v-for="tool in sidebarTools"
            :key="tool.abbr"
            class="list-group-item d-flex align-items-center justify-content-between py-2"
          >
            <span class="d-flex align-items-center">
              <md-icon class="text-muted mr-2">{{ tool.icon }}</md-icon>
              {{ tool.title }}
            </span>
            <b-form-checkbox
              :id="`sidebar-tool-${tool.abbr}`"
              v-model="tool.enable"
              :name="`sidebar-tool-${tool.abbr}`"
              switch
              class="mr-n2"
            />
          </div>

          <div
            v-for="tool in fixedSidebarTools"
            :key="tool.abbr"
            class="list-group-item d-flex align-items-center justify-content-between py-2 text-muted"
          >
            <span class="d-flex align-items-center">
              <md-icon class="text-muted mr-2">{{ tool.icon }}</md-icon>
              {{ tool.title }}
            </span>
            <small>Always on</small>
          </div>
        </div>
      </b-form-group>

      <!-- User Details -->
      <div v-if="!$route.params.id">
        <page-separator title="Admin's Info" />

        <b-form-group
          :label="$t('userMsgs.avatar')"
          label-for="avatar"
          label-class="form-label"
          class="row-align-items-center"
        >
          <b-media class="align-items-center" vertical-align="center">
            <user-avatar
              slot="aside"
              size="lg"
              :isLoading="isAvatarUploading"
              :user="user"
              url-field="avatarUrl"
              first-name-field="firstName"
              last-name-field="lastName"
            >
            </user-avatar>

            <image-uploader
              @image="e => setImage(e, 'avatar')"
              :isUploading="isAvatarUploading"
              :isRequired="false"
              :placeholder="$t('userMsgs.avatarDesc')"
            />
          </b-media>
        </b-form-group>

        <div class="row">
          <b-form-group
            :label="$t('userMsgs.firstName')"
            label-for="firstname"
            label-class="form-label"
            class="col-md-6"
          >
            <b-form-input id="firstname" :placeholder="$t('userMsgs.firstName')" v-model="user.firstName" required />
          </b-form-group>

          <b-form-group :label="$t('userMsgs.lastName')" label-for="lastname" label-class="form-label" class="col-md-6">
            <b-form-input id="lastname" :placeholder="$t('userMsgs.lastName')" v-model="user.lastName" required />
          </b-form-group>
        </div>

        <b-form-group :label="$t('authMsgs.emailAddr')" label-for="email" label-class="form-label">
          <b-form-input
            id="email"
            :placeholder="$t('authMsgs.emailAddr')"
            v-model="user.email"
            type="email"
            autocomplete="off"
            required
            :state="errorStates.userEmail"
          />
          <b-form-invalid-feedback>{{ $t('userMsgs.userAlreadyExists') }}</b-form-invalid-feedback>
        </b-form-group>

        <b-form-group
          :label="$t('authMsgs.password')"
          label-for="password"
          label-class="form-label"
          v-if="!$route.params.id"
        >
          <b-alert variant="soft-primary px-12pt" show>
            <div class="d-flex align-items-center justify-content-between">
              <span class="text-black-100"
                >Auto-generated password: <strong>{{ user.password }}</strong></span
              >

              <span v-if="isPassCopied" class="text-success"><md-icon>check</md-icon> Copied</span>
              <a href="#" @click.prevent="copyPass" v-else v-b-popover.hover.top="'Copy Password'">
                <md-icon>content_copy</md-icon>
              </a>
            </div>
          </b-alert>
        </b-form-group>

        <div class="row" v-else>
          <b-form-group :label="$t('authMsgs.password')" label-for="npass" label-class="form-label" class="col-md-6">
            <b-form-input
              id="npass"
              name="password"
              :placeholder="$t('authMsgs.password')"
              v-model="user.password"
              :required="!!user.repeatPassword"
              :type="fieldTypes.password"
              autocomplete="off"
              @focus="handlePassType"
              @blur="handlePassType"
              :state="errorStates.password"
            />
            <b-form-invalid-feedback>{{ $t(`passValidationMsgs.${passErrorCode}`) }}</b-form-invalid-feedback>
          </b-form-group>

          <b-form-group
            :label="$t('profileMsgs.repeatPassword')"
            label-for="repeatPassword"
            label-class="form-label"
            class="col-md-6"
          >
            <b-form-input
              id="repeatPassword"
              name="repeatPassword"
              :placeholder="$t('profileMsgs.repeatPassword')"
              v-model="user.repeatPassword"
              :required="!!user.password"
              :type="fieldTypes.repeatPassword"
              autocomplete="off"
              @focus="handlePassType"
              @blur="handlePassType"
              :state="errorStates.password"
            />
          </b-form-group>
        </div>
      </div>
      <b-btn variant="primary" :disabled="isFormLoading" style="width: 150px" type="submit" class="btn-normal">
        <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
        <span v-else>{{ $route.params.id ? $t('update') : $t('add') }}</span>
      </b-btn>
    </b-form>
  </div>
</template>

<script>
import axios from 'axios';
import hexRgb from 'hex-rgb';
import { countries } from 'countries-list';
import { get, map } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import { FmvAvatar } from 'fmv-avatar';
import PageHeader from '@/components/Ui/PageHeader.vue';
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import Editor from '@tinymce/tinymce-vue';
import generator from 'generate-password';

import Page from '../../../components/Page.vue';
import UserAvatar from '../../../components/User/UserAvatar.vue';
import VSwatches from 'vue-swatches';
import {
  PASSWORD_VALIDATIONS,
  PASSWORD_ALL_NUMERIC_REGEX,
  PHONE_FORMAT_REGEX,
  USER_ROLES
} from '../../../common/constants';
import { getClickableLink } from '@/common/utils';
import 'vue-swatches/dist/vue-swatches.css';
var UsaStates = require('usa-states').UsaStates;
import ImageUploader from '@/components/ImageUploader.vue';

export default {
  name: 'PostOrganization',
  components: {
    PageHeader,
    PageSeparator,
    FmvAvatar,
    VSwatches,
    UserAvatar,
    Editor,
    ImageUploader
  },
  extends: Page,

  data() {
    return {
      USER_ROLES,
      tinemceApiKey: process.env.VUE_APP_TINEMCE_API_KEY,
      swatches: [
        ['#1FBC9C', '#1CA085', '#2ECC70', '#3398DB'],
        ['#3B86F5', '#8E43AD', '#3D556E', '#222F3D'],
        ['#181818', '#F39C19', '#E84B3C', '#C0382B'],
        ['#DDE6E8', '#BDC3C8', '#C0382B', '#2F6FED']
      ],
      title: this.$route.params.id ? 'Edit Organization' : 'Add Organization',
      user: {
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        repeatPassword: '',
        phone: '',
        avatarUrl: ''
      },
      organization: {
        name: '',
        email: '',
        phone: '',

        zipcode: '',
        address: '',
        city: '',
        state: '',
        allow_emp_deduct: false,
        show_all_school: true,
        hide_purchase_btn: false,
        enable_discussions: false,
        enable_bulk_messaging: false,
        country: 'United States',
        logoUrl: null,
        small_logo_url: null,

        primaryColor: '#3B86F5',
        secondaryColor: '#2F6FED',
        darkColor: '#181818',
        available_tokens: 0,
        available_job_credits: 0,
        bio: '',
        short_bio: '',
        offeredCareers: []
      },
      // Configurable org sidebar tools. `abbr` must match the backend/Sidebar.vue keys.
      sidebarTools: [
        { id: 1, title: 'Discussions', abbr: 'discussions', icon: 'forum', enable: true },
        { id: 2, title: 'Bulk Emails', abbr: 'bulk_emails', icon: 'email', enable: true },
        { id: 3, title: 'Bulk SMS', abbr: 'bulk_sms', icon: 'sms', enable: true },
        { id: 4, title: 'Interviews', abbr: 'interviews', icon: 'mic', enable: true },
        { id: 5, title: 'Research', abbr: 'research', icon: 'menu_book', enable: true },
        { id: 6, title: 'Sponsorships', abbr: 'sponsorships', icon: 'stars', enable: true },
        { id: 7, title: 'Events', abbr: 'events', icon: 'event', enable: true },
        // Hides the whole Website section (Build Site, Website Analytics, View Site).
        { id: 8, title: 'Website', abbr: 'website', icon: 'language', enable: true }
      ],
      // Shown for context only -- these are always visible in the sidebar.
      fixedSidebarTools: [
        { title: 'Jobs', abbr: 'jobs', icon: 'work' },
        { title: 'Resources', abbr: 'resources', icon: 'folder' }
      ],
      is_published: false,
      wbst_domain: '',
      fieldTypes: { password: 'text', repeatPassword: 'text' },
      countryOptions: [],
      stateOptions: [],
      errorStates: {
        password: null,
        userEmail: null,
        phone: null
      },
      passErrorCode: null,

      isPassCopied: false,
      isLoading: false,
      isLogoUploading: false,
      isAvatarUploading: false,
      isSmallLogoUploading: false,
      isProfileBgImgUploading: false,
      uploadFile: {
        logo: null,
        avatar: null,
        smallLogo: null,
        profileBgImage: null
      },
      uploadPercent: { logo: 0, smallLogo: 0, avatar: 0, profileBgImage: 0 },
      uploadCancelTokenSource: {
        logo: null,
        avatar: null,
        smallLogo: null,
        profileBgImage: null
      },

      OrganizationTypeOptions: [
        { value: 'enterprise', text: 'Enterprise' },
        { value: 'association', text: 'Association' },
        { value: 'non_profit', text: 'Non Profit' },
        { value: 'affiliate', text: 'Affiliate' },
        { value: 'other', text: 'Other' }
      ],

      organizationType: '',
      otherOrganizationType: null,
      communityLink: '',
      allCareers: [],
      areCareersLoading: false
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Organizations', to: { name: 'manage-organizations' } },
        {
          text: this.$route.params.id ? 'Edit Organization' : 'Add Organization',
          active: true
        }
      ];
    },
    isFormLoading() {
      return this.isLoading || this.isLogoUploading;
    },
    allSidebarToolsEnabled() {
      return this.sidebarTools.every((tool) => tool.enable);
    }
  },

  methods: {
    toggleAllSidebarTools() {
      const enable = !this.allSidebarToolsEnabled;
      this.sidebarTools.forEach((tool) => {
        tool.enable = enable;
      });
    },
    ...mapActions('organization', [
      'getOrganization',
      'createOrganization',
      'updateOrganization',
      'getOrgImageUploadPresignedUrl'
    ]),
    ...mapActions('s3Upload', ['uploadToPresignedUrl']),
    ...mapActions('careerPreference', ['getAllCareerPreferences']),

    async fetchCareers() {
      this.areCareersLoading = true;

      const response = await this.getAllCareerPreferences({});
      this.allCareers = response.data;

      this.areCareersLoading = false;
    },
    get,
    getClickableLink,
    setImage(file, image) {
      this.uploadFile[image] = file;
      this.uploadImage(image);
    },
    handlePassType(event) {
      const { srcElement, type } = event;
      const { name, value } = srcElement;

      if (type === 'blur' && !value) {
        this.fieldTypes[name] = 'text';
      } else {
        this.fieldTypes[name] = 'password';
      }
    },

    async onSubmit() {
      this.isLoading = true;

      if (!PHONE_FORMAT_REGEX.test(this.organization.phone)) {
        this.errorStates.phone = false;
        this.makeToast({ variant: 'danger', msg: 'Please fill the form correctly.' });
      } else if (this.user.password !== this.user.repeatPassword) {
        this.passErrorCode = PASSWORD_VALIDATIONS.PASSWORDS_NOT_MATCH;
        this.errorStates.password = false;
        this.makeToast({ variant: 'danger', msg: 'Please fill the form correctly.' });
      } else if (this.user.password && this.user.password.length < 5) {
        this.passErrorCode = PASSWORD_VALIDATIONS.PASSWORDS_MIN_LENGTH;
        this.errorStates.password = false;
        this.makeToast({ variant: 'danger', msg: 'Please fill the form correctly.' });
      } else if (this.user.password && PASSWORD_ALL_NUMERIC_REGEX.test(this.user.password)) {
        this.passErrorCode = PASSWORD_VALIDATIONS.PASSWORDS_ALL_NUMERIC;
        this.errorStates.password = false;
        this.makeToast({ variant: 'danger', msg: 'Please fill the form correctly.' });
      } else {
        try {
          const data = {
            name: this.organization.name,
            contact_email: this.organization.email,
            is_active: true,
            zipcode: this.organization.zipcode,
            address: this.organization.address,
            city: this.organization.city,
            state: this.organization.state,
            country: this.organization.country,
            allow_emp_deduct: this.organization.allow_emp_deduct,
            show_all_school: this.organization.show_all_school,
            logo_url: this.organization.logoUrl,
            small_logo_url: this.organization.small_logo_url,
            profile_bg_img_url: this.organization.profile_bg_img_url,
            primary_color: this.organization.primaryColor,
            secondary_color: this.organization.secondaryColor,
            dark_color: this.organization.darkColor,
            dark_color_rgb: hexRgb(this.organization.darkColor, { alpha: 0.5 }),
            available_job_credits: this.organization.available_job_credits,
            available_tokens: this.organization.available_tokens,
            bio: this.organization.bio,
            short_bio: this.organization.short_bio,
            phone: this.organization.phone,
            type: this.organizationType,
            hide_purchase_btn: this.organization.hide_purchase_btn,
            enable_discussions: this.organization.enable_discussions,
            enable_bulk_messaging: this.organization.enable_bulk_messaging,
            sidebar_tools_config: { sidebar_tools: this.sidebarTools },
            other_organization_type: this.otherOrganizationType,
            organization_site: {
              wbst_domain: this.wbst_domain,
              is_published: this.is_published
            },
            community_link: this.communityLink ? getClickableLink(this.communityLink) : '',
            offered_careers: this.organization.offeredCareers
          };

          if (this.$route.params.id) {
            await this.updateOrganization({
              id: this.$route.params.id,
              data
            });
            this.makeToast({ variant: 'success', msg: 'Organization Updated Successfully!' });
          } else {
            await this.createOrganization({
              ...data,
              org_owner: {
                email: this.user.email,
                first_name: this.user.firstName,
                last_name: this.user.lastName,
                ...(this.user.password && { password: this.user.password }),
                phone: this.user.phone || this.organization.phone,
                avatar_url: this.user.avatarUrl
              }
            });
            this.makeToast({ variant: 'success', msg: 'Organization Added Successfully!' });
          }
          setTimeout(() => this.$router.push({ name: 'manage-organizations' }), 250);
        } catch (err) {
          if (get(err, 'response.data.main_admin')) {
            this.errorStates.ownerEmail = false;
          }
          this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        }
      }

      this.isLoading = false;
    },
    async copyPass() {
      try {
        await navigator.clipboard.writeText(this.user.password);
        this.isPassCopied = true;
        setTimeout(() => (this.isPassCopied = false), 2000);
      } catch ($e) {
        this.makeToast({ variant: 'danger', msg: 'Unable to copy.' });
      }
    },
    async uploadImage(uploadType) {
      this.uploadCancelTokenSource[uploadType] = axios.CancelToken.source();
      this.isLogoUploading = uploadType === 'logo';
      this.isSmallLogoUploading = uploadType === 'smallLogo';
      this.isAvatarUploading = uploadType === 'avatar';
      this.isProfileBgImgUploading = uploadType === 'profileBgImage';
      try {
        const urlResp = await this.getOrgImageUploadPresignedUrl({
          file_name: this.uploadFile[uploadType].name,
          content_type: this.uploadFile[uploadType].type
        });
        await this.uploadToPresignedUrl({
          url: urlResp.upload_url,
          file: this.uploadFile[uploadType],
          config: {
            onUploadProgress: function(progressEvent) {
              this.uploadPercent[uploadType] = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            }.bind(this),
            cancelToken: this.uploadCancelTokenSource[uploadType].token
          }
        });
        if (uploadType === 'logo') {
          this.organization.logoUrl = urlResp.upload_url.split('?')[0];
        } else if (uploadType === 'smallLogo') {
          this.organization.small_logo_url = urlResp.upload_url.split('?')[0];
        } else if (uploadType === 'profileBgImage') {
          this.organization.profile_bg_img_url = urlResp.upload_url.split('?')[0];
        } else {
          this.user.avatarUrl = urlResp.upload_url.split('?')[0];
        }
      } catch (error) {
        this.uploadFile[uploadType] = null;
      }

      this.uploadCancelTokenSource[uploadType] = null;
      this.uploadPercent[uploadType] = 0;

      if (uploadType === 'logo') this.isLogoUploading = false;
      else if (uploadType === 'smallLogo') this.isSmallLogoUploading = false;
      else if (uploadType === 'profileBgImage') this.isProfileBgImgUploading = false;
      else this.isAvatarUploading = false;
    }
  },

  async mounted() {
    this.isLoading = true;
    this.fetchCareers();

    var usStates = new UsaStates();
    this.stateOptions = this.stateOptions.concat(
      map(usStates.states, state => ({
        value: state.name,
        text: state.name
      }))
    );
    this.countryOptions = this.countryOptions.concat(
      map(countries, country => ({
        value: country.name,
        text: country.name
      }))
    );

    // Auto-generate password.
    if (!this.$route.params.id) {
      this.user.password = this.user.repeatPassword = generator.generate({ length: 10, numbers: true, strict: true });
    }

    try {
      if (this.$route.params.id) {
        const resp = (await this.getOrganization(this.$route.params.id)).data;
        this.organization.name = resp.name;
        this.organization.email = resp.contact_email;
        this.organization.phone = resp.phone;

        this.organization.zipcode = resp.zipcode;
        this.organization.address = resp.address;
        this.organization.city = resp.city;
        this.organization.state = resp.state;
        this.organization.country = resp.country;
        this.organization.logoUrl = resp.logo_url;
        this.organization.small_logo_url = resp.small_logo_url;
        this.organization.profile_bg_img_url = resp.profile_bg_img_url;
        this.organization.primaryColor = resp.primary_color;
        this.organization.secondaryColor = resp.secondary_color;
        this.organization.darkColor = resp.dark_color;
        this.wbst_domain = resp.organization_site.wbst_domain;
        this.is_published = resp.organization_site.is_published;
        this.organization.bio = resp.bio;
        this.organization.short_bio = resp.short_bio;
        this.organization.available_tokens = resp.available_tokens;
        this.organization.available_job_credits = resp.available_job_credits;
        this.organizationType = resp.type;
        this.organization.allow_emp_deduct = resp.allow_emp_deduct;
        this.organization.show_all_school = resp.show_all_school;
        this.organization.hide_purchase_btn = resp.hide_purchase_btn;
        this.organization.enable_discussions = resp.enable_discussions;
        this.organization.enable_bulk_messaging = resp.enable_bulk_messaging;
        // Merge saved toggles onto the defaults so tools added later still show up.
        const savedTools = get(resp, 'sidebar_tools_config.sidebar_tools') || [];
        this.sidebarTools = this.sidebarTools.map((tool) => {
          const saved = savedTools.find((t) => t.abbr === tool.abbr);
          return saved ? { ...tool, enable: saved.enable } : tool;
        });
        this.communityLink = resp.community_link;
        this.organization.offeredCareers = get(resp, 'offered_careers');
      }
    } catch (e) {
      this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      this.$router.push({ name: 'manage-organizations' });
    }

    this.isLoading = false;
  }
};
</script>
